// src/components/WisPanCompanions.js
/**
 * WisPanCompanions - Animated companion characters
 * 
 * Features:
 * - Pan the Panda (left) - Practice encouragement & timer warnings
 * - Wis the Owl (right) - Musical wisdom & tips
 * - Auto-dismiss messages after 3-4 seconds
 * - Smooth fade-in/fade-out animations
 * - Bounce animation for new messages
 * - Priority-based message display
 * 
 * Usage:
 * <WisPanCompanions
 *   panMessage="You're doing great! 🐼"
 *   wisMessage="Remember: Practice makes perfect! 🦉"
 *   autoDismissPan={true}
 *   autoDismissWis={true}
 *   dismissAfterMs={4000}
 * />
 */

import React, { useState, useEffect, useRef } from "react";

export default function WisPanCompanions({ 
  panMessage = null,
  wisMessage = null,
  autoDismissPan = true,
  autoDismissWis = true,
  dismissAfterMs = 4000,
  onPanDismiss = () => {},
  onWisDismiss = () => {},
  showPan = true,
  showWis = true,
  panReaction = null, // 'happy', 'excited', 'worried', 'celebrating'
  wisReaction = null,
}) {
  const [displayPanMessage, setDisplayPanMessage] = useState(panMessage);
  const [displayWisMessage, setDisplayWisMessage] = useState(wisMessage);
  const [isPanVisible, setIsPanVisible] = useState(false);
  const [isWisVisible, setIsWisVisible] = useState(false);
  
  const panTimeoutRef = useRef(null);
  const wisTimeoutRef = useRef(null);
  
  // Pan message effect
  useEffect(() => {
    if (panMessage) {
      // Show new message
      setDisplayPanMessage(panMessage);
      setIsPanVisible(true);
      
      // Clear existing timeout
      if (panTimeoutRef.current) {
        clearTimeout(panTimeoutRef.current);
      }
      
      // Auto-dismiss if enabled
      if (autoDismissPan) {
        panTimeoutRef.current = setTimeout(() => {
          setIsPanVisible(false);
          // Keep message for fade-out animation, then clear
          setTimeout(() => {
            setDisplayPanMessage(null);
            onPanDismiss();
          }, 300); // Match CSS transition duration
        }, dismissAfterMs);
      }
    } else {
      setIsPanVisible(false);
      setTimeout(() => setDisplayPanMessage(null), 300);
    }
    
    return () => {
      if (panTimeoutRef.current) {
        clearTimeout(panTimeoutRef.current);
      }
    };
  }, [panMessage, autoDismissPan, dismissAfterMs, onPanDismiss]);
  
  // Wis message effect
  useEffect(() => {
    if (wisMessage) {
      setDisplayWisMessage(wisMessage);
      setIsWisVisible(true);
      
      if (wisTimeoutRef.current) {
        clearTimeout(wisTimeoutRef.current);
      }
      
      if (autoDismissWis) {
        wisTimeoutRef.current = setTimeout(() => {
          setIsWisVisible(false);
          setTimeout(() => {
            setDisplayWisMessage(null);
            onWisDismiss();
          }, 300);
        }, dismissAfterMs);
      }
    } else {
      setIsWisVisible(false);
      setTimeout(() => setDisplayWisMessage(null), 300);
    }
    
    return () => {
      if (wisTimeoutRef.current) {
        clearTimeout(wisTimeoutRef.current);
      }
    };
  }, [wisMessage, autoDismissWis, dismissAfterMs, onWisDismiss]);
  
  // Reaction emoji map
  const getReactionEmoji = (reaction) => {
    const reactions = {
      happy: '😊',
      excited: '🤩',
      worried: '😰',
      celebrating: '🎉',
      thinking: '🤔',
      proud: '🥰',
    };
    return reactions[reaction] || null;
  };
  
  return (
    <div className="fixed bottom-4 left-4 right-4 flex justify-between items-end z-50 px-4 pointer-events-none">
      {/* Pan the Panda */}
      {showPan && (
        <div className="relative flex flex-col items-center pointer-events-auto">
          {/* Message bubble */}
          {displayPanMessage && (
            <div 
              className={`
                bg-white text-black p-3 rounded-2xl shadow-lg text-xs sm:text-sm mb-2 
                max-w-[140px] sm:max-w-[180px] text-center border-2 border-purple-200
                transition-all duration-300 ease-in-out
                ${isPanVisible 
                  ? 'opacity-100 translate-y-0 scale-100' 
                  : 'opacity-0 translate-y-2 scale-95'
                }
              `}
              style={{
                animation: isPanVisible ? 'bounce-in 0.5s ease-out' : 'none',
              }}
            >
              {displayPanMessage}
            </div>
          )}
          
          {/* Pan character */}
          <div className="relative">
            <img
              src="/images/pan-the-panda.png"
              alt="Pan the Panda"
              className={`
                w-16 h-16 sm:w-20 sm:h-20 object-contain drop-shadow-lg
                transition-transform duration-300
                ${isPanVisible ? 'scale-110' : 'scale-100'}
              `}
              style={{
                filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))',
              }}
            />
            
            {/* Reaction emoji */}
            {panReaction && (
              <div className="absolute -top-2 -right-2 text-2xl animate-bounce">
                {getReactionEmoji(panReaction)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Wis the Owl */}
      {showWis && (
        <div className="relative flex flex-col items-center pointer-events-auto">
          {/* Message bubble */}
          {displayWisMessage && (
            <div 
              className={`
                bg-white text-black p-3 rounded-2xl shadow-lg text-xs sm:text-sm mb-2 
                max-w-[140px] sm:max-w-[180px] text-center border-2 border-blue-200
                transition-all duration-300 ease-in-out
                ${isWisVisible 
                  ? 'opacity-100 translate-y-0 scale-100' 
                  : 'opacity-0 translate-y-2 scale-95'
                }
              `}
              style={{
                animation: isWisVisible ? 'bounce-in 0.5s ease-out' : 'none',
              }}
            >
              {displayWisMessage}
            </div>
          )}
          
          {/* Wis character */}
          <div className="relative">
            <img
              src="/images/wis.png"
              alt="Wis the Owl"
              className={`
                w-16 h-16 sm:w-20 sm:h-20 object-contain drop-shadow-lg
                transition-transform duration-300
                ${isWisVisible ? 'scale-110' : 'scale-100'}
              `}
              style={{
                filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))',
              }}
            />
            
            {/* Reaction emoji */}
            {wisReaction && (
              <div className="absolute -top-2 -right-2 text-2xl animate-bounce">
                {getReactionEmoji(wisReaction)}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Inline styles for bounce animation */}
      <style>{`
        @keyframes bounce-in {
          0% {
            transform: translateY(20px) scale(0.9);
            opacity: 0;
          }
          50% {
            transform: translateY(-5px) scale(1.05);
          }
          100% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}